/**
 * useUsageLimits — shared tenant quota-usage source for page-level usage chips.
 *
 * All 8 meters (contacts/leads, agents, users, roles, storage, messages, apiCalls,
 * aiTokens) come from ONE `/admin/billing/usage-status` call, cached module-wide so
 * every <UsageChip> on a page (and across pages) shares a single request. Call
 * `refreshUsage()` after a mutation that changes a count (e.g. a contact import) to
 * re-pull fresh numbers.
 */
import { useCallback, useEffect, useState } from 'react';
import adminApi, { BillingUsageStatus, PlanLimitStatus } from '@/services/admin.api';

export type UsageResource = keyof BillingUsageStatus;

/** After this age the cache is refetched in the background on the next mount, so a
 *  count that changed on another page (e.g. a contact import) shows up on arrival. */
const STALE_MS = 10_000;

let cache: BillingUsageStatus | null = null;
let cacheAt = 0;
let inflight: Promise<BillingUsageStatus> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

function isStale(): boolean {
  return Date.now() - cacheAt > STALE_MS;
}

function load(force = false): Promise<BillingUsageStatus> {
  if (!force && cache && !isStale()) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = adminApi
    // Rendered as a decorative header widget on MANY pages (UsageChip) —
    // never a page's primary content, so a permission denial must degrade
    // quietly (chip just doesn't render — see UsageChip's `if (!stat) return
    // null`) rather than interrupt every page that happens to show one.
    .getUsageStatus({ suppressPermissionLock: true })
    .then((data) => {
      cache = data;
      cacheAt = Date.now();
      inflight = null;
      notify();
      return data;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Invalidate + re-fetch the shared usage cache (fire-and-forget). */
export function refreshUsage(): void {
  void load(true);
}

/**
 * Overwrite one meter from a server push, with NO network round trip.
 *
 * Some quotas only move because of something that happened server-side with no
 * client action to observe — inbound media is the clearest case: a customer
 * sends a 5 MB video, the worker either stores it or refuses it, and every open
 * tab's meter is instantly wrong with nothing to invalidate it. `refreshUsage()`
 * would fix that but costs a request per event and lands a frame late.
 *
 * The socket event already carries the authoritative numbers, so apply them
 * directly and notify — the chip re-renders in the same tick the event arrives.
 * `cacheAt` is deliberately NOT reset: this is a patch of one field, not a
 * complete refetch, so the next mount should still refresh a stale cache.
 *
 * A no-op before the first load, since there is no cache to patch and the
 * initial fetch is already on its way with fresher numbers than this event.
 */
export function patchUsage(resource: UsageResource, next: Partial<PlanLimitStatus>): void {
  if (!cache) return;
  const current = cache[resource];
  if (!current) return;
  cache = { ...cache, [resource]: { ...current, ...next } };
  notify();
}

export function useUsageLimits(): {
  usage: BillingUsageStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [usage, setUsage] = useState<BillingUsageStatus | null>(cache);
  const [loading, setLoading] = useState<boolean>(!cache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const sync = () => {
      if (alive) setUsage(cache);
    };
    listeners.add(sync);

    if (!cache) {
      setLoading(true);
      load()
        .then(() => {
          if (alive) setLoading(false);
        })
        .catch((e: any) => {
          if (alive) {
            setError(e?.message ?? 'Failed to load usage');
            setLoading(false);
          }
        });
    } else if (isStale()) {
      // Cached but old — refresh in the background; listeners push the fresh numbers.
      load().catch(() => {});
    }

    return () => {
      alive = false;
      listeners.delete(sync);
    };
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    load(true)
      .catch((e: any) => setError(e?.message ?? 'Failed to load usage'))
      .finally(() => setLoading(false));
  }, []);

  return { usage, loading, error, refresh };
}
